import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDateTime, formatStatus } from '@/lib/utils';
import { Activity, Search } from 'lucide-react';

const ENTITY_TYPES = ['DWR', 'LEAD', 'PROJECT', 'EXPENSE', 'AMC'];

export default function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = () => {
    const params = new URLSearchParams();
    if (filterEntity) params.set('entity_type', filterEntity);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    api.get(`/utility/audit-logs?${params}`).then(r => setLogs(r.data.logs || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(); }, [filterEntity, dateFrom, dateTo]);

  if (!['DIRECTOR', 'HR_ADMIN'].includes(user?.role)) {
    return <div className="text-center py-12 text-slate-500">Only Directors and HR Admins can view audit logs.</div>;
  }

  return (
    <div data-testid="audit-log-page" className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Audit Trail</h1>
        <p className="text-sm text-slate-500">{logs.length} log entries</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-entity-type">
          <option value="">All Types</option>
          {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" placeholder="From" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" placeholder="To" />
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Timestamp</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">User</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Entity</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Action</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Description</th>
            </tr></thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                  <td className="py-2.5 px-4 text-xs text-slate-500">{formatDateTime(log.timestamp)}</td>
                  <td className="py-2.5 px-4 text-slate-900 font-medium">{log.user_name}</td>
                  <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{log.entity_type}</span></td>
                  <td className="py-2.5 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${log.action === 'CREATE' ? 'bg-green-100 text-green-700' : log.action === 'APPROVE' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{log.action}</span></td>
                  <td className="py-2.5 px-4 text-slate-700">{log.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {logs.length === 0 && <div className="text-center py-12 text-slate-400">No audit logs found</div>}
        </div>
      )}
    </div>
  );
}
